import React, { useDeferredValue, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Search } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { sitemap } from "./sitemapData";
import "../styles/SiteMapCards.css";

const sectionThemes = {
  "Utah Verticals": {
    accent: "#0f766e",
    soft: "#ccfbf1",
    glow: "rgba(15, 118, 110, 0.16)",
  },
  "Utah At a Glance": {
    accent: "#2563eb",
    soft: "#dbeafe",
    glow: "rgba(37, 99, 235, 0.16)",
  },
  "Other Pages": {
    accent: "#ea580c",
    soft: "#ffedd5",
    glow: "rgba(234, 88, 12, 0.16)",
  },
};

const getSectionTheme = (label) =>
  sectionThemes[label] || {
    accent: "#0891b2",
    soft: "#cffafe",
    glow: "rgba(8, 145, 178, 0.16)",
  };

const filterNode = (node, query) => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return node;
  }

  const matchingChildren = (node.children || [])
    .map((child) => filterNode(child, normalizedQuery))
    .filter(Boolean);

  const selfMatches =
    node.label.toLowerCase().includes(normalizedQuery) ||
    (node.path || "").toLowerCase().includes(normalizedQuery);

  if (selfMatches || matchingChildren.length) {
    return {
      ...node,
      children: matchingChildren,
    };
  }

  return null;
};

const buildVisibleTree = (rootNode, activeSection, query) => {
  const scopedRoot =
    activeSection === "all"
      ? rootNode
      : {
          ...rootNode,
          children: (rootNode.children || []).filter(
            (section) => section.label === activeSection,
          ),
        };

  return filterNode(scopedRoot, query) || {
    ...scopedRoot,
    children: [],
  };
};

const countVisiblePages = (nodes = []) =>
  nodes.reduce(
    (total, node) => total + 1 + countVisiblePages(node.children || []),
    0,
  );

const TreeNode = ({ node, sectionLabel, level = 0, root = false }) => {
  const hasChildren = Boolean(node.children?.length);
  const nodeType = root ? "Section" : hasChildren ? "Branch" : "Page";

  return (
    <article
      className={`sitemap-tree-node level-${Math.min(level, 4)}${
        root ? " is-root" : ""
      }${
        hasChildren ? " has-children" : ""
      }`}
    >
      <div className="sitemap-tree-nodeCard">
        <div className="sitemap-tree-nodeTop">
          <div className="sitemap-tree-nodeEyebrow">
            <span>{nodeType}</span>
            {!root ? <span>{sectionLabel}</span> : null}
          </div>
          {node.path ? (
            <Link
              to={node.path}
              className="sitemap-tree-openLink"
            >
              <ArrowUpRight size={14} />
            </Link>
          ) : null}
        </div>

        <div>
          <h3>{node.label}</h3>
          {node.path ? <p>{node.path}</p> : null}
        </div>

        <div className="sitemap-tree-nodeMeta">
          {hasChildren ? (
            <span>
              {node.children?.length || 0} child
              {node.children?.length === 1 ? "" : "ren"}
            </span>
          ) : null}
        </div>

        {node.path ? (
          <Link
            to={node.path}
            className="sitemap-tree-nodeAction"
          >
            Open
            <ArrowRight size={14} />
          </Link>
        ) : null}
      </div>

      {hasChildren ? (
        <div className="sitemap-tree-children">
          {node.children.map((child) => (
            <TreeNode
              key={`${child.label}-${child.path || "group"}`}
              node={child}
              sectionLabel={sectionLabel}
              level={level + 1}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
};

function SiteMapCards() {
  const rootNode = sitemap[0];
  const availableSections = rootNode.children || [];
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSection, setActiveSection] = useState("all");
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const visibleTree = useMemo(
    () => buildVisibleTree(rootNode, activeSection, deferredSearchTerm),
    [activeSection, deferredSearchTerm, rootNode],
  );

  const visibleSections = visibleTree.children || [];
  const visibleCount = useMemo(
    () => countVisiblePages(visibleSections),
    [visibleSections],
  );

  return (
    <div className="sitemap-cards-page">
      <Header />

      <main className="sitemap-cards-main">
        <section className="sitemap-cards-shell">
          <div className="sitemap-cards-hero">
            <div>
              <span className="sitemap-cards-kicker">Sitemap Tree</span>
              <h1>Beautiful tree-based page directory.</h1>
              <p>
                Browse UtahData.org through a cleaner connected sitemap with
                searchable sections, nested nodes, and direct page links.
              </p>
            </div>

            <div className="sitemap-cards-summary">
              <strong>{visibleCount}</strong>
              <span>Visible pages</span>
            </div>
          </div>

          <div className="sitemap-cards-toolbar">
            <label className="sitemap-cards-search">
              <Search size={17} />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search titles or route paths"
                aria-label="Search sitemap cards"
              />
            </label>

            <div className="sitemap-cards-filters">
              <button
                type="button"
                className={activeSection === "all" ? "active" : ""}
                onClick={() => setActiveSection("all")}
              >
                All sections
              </button>
              {availableSections.map((section) => (
                <button
                  key={section.label}
                  type="button"
                  className={activeSection === section.label ? "active" : ""}
                  onClick={() => setActiveSection(section.label)}
                >
                  {section.label}
                </button>
              ))}
            </div>
          </div>

          <div className="sitemap-cards-sections">
            {visibleSections.length ? (
              visibleSections.map((section) => (
                <section
                  key={`${section.label}-${section.path || "group"}`}
                  className="sitemap-cards-section"
                  style={{
                    "--section-accent": getSectionTheme(section.label).accent,
                    "--section-soft": getSectionTheme(section.label).soft,
                    "--section-glow": getSectionTheme(section.label).glow,
                  }}
                >
                  <div className="sitemap-cards-sectionHeader">
                    <div>
                      <span className="sitemap-cards-sectionTag">Section</span>
                      <h2>{section.label}</h2>
                      <p>{section.path || "Grouped navigation section"}</p>
                    </div>
                  </div>

                  <div className="sitemap-tree-board">
                    <TreeNode
                      node={section}
                      sectionLabel={section.label}
                      root
                    />
                  </div>
                </section>
              ))
            ) : (
              <div className="sitemap-cards-empty">
                <h2>No matching sitemap items</h2>
                <p>Try a shorter search term or switch back to all sections.</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default SiteMapCards;
